<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { ChevronLeft, ChevronRight, Pause, Play, X } from 'lucide-vue-next'
import { useAppStore } from '@/stores/app'
import type { MediaPreviewItem, MediaPreviewType } from '../types'
import type { ComponentPublicInstance } from 'vue'

interface Props {
  visible: boolean
  items: MediaPreviewItem[]
  initialIndex?: number
}

const props = withDefaults(defineProps<Props>(), {
  initialIndex: 0,
})

const emit = defineEmits<{
  'update:visible': [value: boolean]
  close: []
  change: [index: number]
}>()

const appStore = useAppStore()
const currentIndex = ref(0)
const dragOffsetX = ref(0)
const isSnapping = ref(false)
const isDragging = ref(false)

const zoomScale = ref(1)
const zoomTranslateX = ref(0)
const zoomTranslateY = ref(0)
const isCurrentVideoPlaying = ref(false)
const videoRefs = new Map<number, HTMLVideoElement>()
let pinchStartDistance = 0
let pinchStartScale = 1
let swipeStartX = 0
let swipeStartTime = 0
let panStartX = 0
let panStartY = 0
let panOriginX = 0
let panOriginY = 0
let wheelLock = false

const animMs = 260
const maxScale = 4

const normalizeType = (item: MediaPreviewItem): MediaPreviewType => {
  if (item.type === 'image' || item.type === 'video') {
    return item.type
  }

  const url = item.url.toLowerCase()
  if (/(\.jpg|\.jpeg|\.png|\.webp|\.gif|\.bmp|\.avif)(\?|$)/.test(url)) {
    return 'image'
  }

  return 'video'
}

const safeItems = computed(() => props.items.filter(item => item.url))
const currentItem = computed(() => safeItems.value[currentIndex.value] ?? null)
const currentType = computed(() => {
  if (!currentItem.value) {
    return 'video' as MediaPreviewType
  }
  return normalizeType(currentItem.value)
})
const canPrev = computed(() => currentIndex.value > 0)
const canNext = computed(() => currentIndex.value < safeItems.value.length - 1)
const isImageZooming = computed(() => currentType.value === 'image' && zoomScale.value > 1)

const renderedIndices = computed(() => {
  const total = safeItems.value.length
  if (total === 0) return []

  const start = Math.max(0, currentIndex.value - 1)
  const end = Math.min(total - 1, currentIndex.value + 1)
  const result: number[] = []

  for (let index = start; index <= end; index++) {
    result.push(index)
  }

  return result
})

const trackStyle = computed(() => {
  const baseX = currentIndex.value * 100
  const viewportWidth = window.innerWidth || 1
  const offsetPercent = (dragOffsetX.value / viewportWidth) * 100

  return {
    transform: `translateX(calc(-${baseX}% + ${offsetPercent}%))`,
    transition: isSnapping.value ? `transform ${animMs}ms ease` : 'none',
  }
})

const imageStyle = computed(() => ({
  transform: `translate(${zoomTranslateX.value}px, ${zoomTranslateY.value}px) scale(${zoomScale.value})`,
  transition: isDragging.value ? 'none' : 'transform 180ms ease',
}))

const resetZoom = () => {
  zoomScale.value = 1
  zoomTranslateX.value = 0
  zoomTranslateY.value = 0
}

const setVideoRef = (element: Element | ComponentPublicInstance | null, index: number) => {
  if (element instanceof HTMLVideoElement) {
    videoRefs.set(index, element)
    return
  }

  videoRefs.delete(index)
}

const getCurrentVideo = () => videoRefs.get(currentIndex.value) ?? null

const syncCurrentVideoState = () => {
  const video = getCurrentVideo()
  isCurrentVideoPlaying.value = !!video && !video.paused && !video.ended
}

const pauseAllVideos = () => {
  videoRefs.forEach(video => {
    video.pause()
  })
  isCurrentVideoPlaying.value = false
}

const playCurrentVideo = async () => {
  if (!props.visible || currentType.value !== 'video') return

  const currentVideo = getCurrentVideo()
  if (!currentVideo) return

  videoRefs.forEach((video, index) => {
    if (index !== currentIndex.value) {
      video.pause()
    }
  })

  try {
    await currentVideo.play()
  } catch {
    // 忽略自动播放被浏览器策略拦截的情况
  }

  syncCurrentVideoState()
}

const toggleCurrentVideo = () => {
  if (!props.visible || currentType.value !== 'video') return

  const currentVideo = getCurrentVideo()
  if (!currentVideo) return

  if (currentVideo.paused || currentVideo.ended) {
    void playCurrentVideo()
    return
  }

  currentVideo.pause()
  syncCurrentVideoState()
}

const handleVideoPlay = (index: number) => {
  if (index !== currentIndex.value) return
  isCurrentVideoPlaying.value = true
}

const handleVideoPause = (index: number) => {
  if (index !== currentIndex.value) return
  isCurrentVideoPlaying.value = false
}

const clampIndex = (index: number) => {
  if (safeItems.value.length === 0) return 0
  return Math.max(0, Math.min(index, safeItems.value.length - 1))
}

const closePreview = () => {
  emit('update:visible', false)
  emit('close')
  pauseAllVideos()
  resetZoom()
}

const updateIndex = (index: number) => {
  const nextIndex = clampIndex(index)
  if (nextIndex === currentIndex.value) {
    dragOffsetX.value = 0
    return
  }

  currentIndex.value = nextIndex
  dragOffsetX.value = 0
  pauseAllVideos()
  resetZoom()
  emit('change', nextIndex)
}

const prev = () => updateIndex(currentIndex.value - 1)
const next = () => updateIndex(currentIndex.value + 1)

const applySwipe = () => {
  const duration = Date.now() - swipeStartTime
  const distance = Math.abs(dragOffsetX.value)
  const viewportWidth = window.innerWidth || 1
  const ratio = distance / viewportWidth

  let threshold = 0.5

  if (duration < 150) {
    threshold = 0.03
  } else if (duration < 300) {
    threshold = 0.18
  } else if (duration < 500) {
    threshold = 0.3
  }

  if (ratio > threshold) {
    if (dragOffsetX.value > 0) {
      prev()
    } else {
      next()
    }
  }

  dragOffsetX.value = 0
}

const onTouchStart = (event: TouchEvent) => {
  if (!props.visible || safeItems.value.length === 0) return

  const touches = event.touches

  if (touches.length === 2 && currentType.value === 'image') {
    const first = touches[0]
    const second = touches[1]
    if (!first || !second) return
    pinchStartDistance = Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY)
    pinchStartScale = zoomScale.value
    isDragging.value = true
    return
  }

  if (touches.length !== 1) return

  const touch = touches[0]
  if (!touch) return
  swipeStartX = touch.clientX
  swipeStartTime = Date.now()

  if (isImageZooming.value) {
    panStartX = touch.clientX
    panStartY = touch.clientY
    panOriginX = zoomTranslateX.value
    panOriginY = zoomTranslateY.value
  }

  isDragging.value = true
  isSnapping.value = false
}

const onTouchMove = (event: TouchEvent) => {
  if (!isDragging.value || !props.visible) return

  const touches = event.touches

  if (touches.length === 2 && currentType.value === 'image') {
    event.preventDefault()
    const first = touches[0]
    const second = touches[1]
    if (!first || !second) return
    const distance = Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY)

    if (!pinchStartDistance) {
      pinchStartDistance = distance
      pinchStartScale = zoomScale.value
      return
    }

    const nextScale = pinchStartScale * (distance / pinchStartDistance)
    zoomScale.value = Math.max(1, Math.min(maxScale, nextScale))
    if (zoomScale.value === 1) {
      zoomTranslateX.value = 0
      zoomTranslateY.value = 0
    }
    return
  }

  if (touches.length !== 1) return

  const touch = touches[0]
  if (!touch) return

  if (isImageZooming.value) {
    event.preventDefault()
    zoomTranslateX.value = panOriginX + touch.clientX - panStartX
    zoomTranslateY.value = panOriginY + touch.clientY - panStartY
    return
  }

  event.preventDefault()
  dragOffsetX.value = touch.clientX - swipeStartX
}

const onTouchEnd = () => {
  if (!isDragging.value) return

  isDragging.value = false
  isSnapping.value = true

  if (!isImageZooming.value) {
    applySwipe()
  }

  pinchStartDistance = 0

  window.setTimeout(() => {
    isSnapping.value = false
  }, animMs)
}

const onWheel = (event: WheelEvent) => {
  if (!props.visible || safeItems.value.length === 0) return

  event.preventDefault()

  if (currentType.value === 'image') {
    const delta = event.deltaY
    const nextScale = zoomScale.value + (delta > 0 ? -0.2 : 0.2)
    zoomScale.value = Math.max(1, Math.min(maxScale, nextScale))

    if (zoomScale.value === 1) {
      zoomTranslateX.value = 0
      zoomTranslateY.value = 0
    }
    return
  }

  if (wheelLock) return

  wheelLock = true
  isSnapping.value = true

  if (event.deltaY > 0) {
    next()
  } else {
    prev()
  }

  window.setTimeout(() => {
    wheelLock = false
    isSnapping.value = false
  }, animMs + 40)
}

const onKeydown = (event: KeyboardEvent) => {
  if (!props.visible) return

  if (event.key === 'Escape') {
    closePreview()
    return
  }

  if (event.key === 'ArrowLeft') {
    prev()
    return
  }

  if (event.key === 'ArrowRight') {
    next()
  }
}

watch(
  () => props.visible,
  visible => {
    if (!visible) {
      pauseAllVideos()
      resetZoom()
      return
    }

    currentIndex.value = clampIndex(props.initialIndex)
    dragOffsetX.value = 0
    isSnapping.value = false
    resetZoom()
  },
  { immediate: true },
)

watch(
  () => props.initialIndex,
  index => {
    if (!props.visible) return
    currentIndex.value = clampIndex(index)
    dragOffsetX.value = 0
    pauseAllVideos()
    resetZoom()
  },
)

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <div
    v-if="visible"
    class="touch-none"
    :class="appStore.isMobile ? 'fixed inset-0 z-90' : 'absolute inset-0 z-40'"
    tabindex="0"
    @wheel="onWheel"
  >
    <div class="absolute inset-0 bg-black/90"></div>

    <button
      type="button"
      class="absolute right-4 z-10 inline-flex size-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur"
      :class="appStore.isMobile ? 'top-[calc(env(safe-area-inset-top,0px)+12px)]' : 'top-4'"
      @click="closePreview"
    >
      <X :size="20" />
    </button>

    <button
      v-if="!appStore.isMobile"
      type="button"
      class="absolute left-4 top-1/2 z-10 inline-flex size-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur disabled:cursor-not-allowed disabled:opacity-30"
      :disabled="!canPrev"
      @click="prev"
    >
      <ChevronLeft :size="24" />
    </button>

    <button
      v-if="!appStore.isMobile"
      type="button"
      class="absolute right-4 top-1/2 z-10 inline-flex size-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur disabled:cursor-not-allowed disabled:opacity-30"
      :disabled="!canNext"
      @click="next"
    >
      <ChevronRight :size="24" />
    </button>

    <div
      class="relative h-full w-full overflow-hidden"
      @touchstart="onTouchStart"
      @touchmove="onTouchMove"
      @touchend="onTouchEnd"
      @touchcancel="onTouchEnd"
    >
      <div class="flex h-full w-full" :style="trackStyle">
        <div
          v-for="index in renderedIndices"
          :key="safeItems[index]?.id"
          class="h-full w-full shrink-0 px-2 py-6"
          :class="appStore.isMobile ? 'pt-16 pb-10' : 'py-8 px-16'"
        >
          <div class="relative flex h-full w-full items-center justify-center overflow-hidden">
            <img
              v-if="normalizeType(safeItems[index]!) === 'image'"
              :src="safeItems[index]!.url"
              :alt="safeItems[index]!.title || 'preview-image'"
              class="max-h-full max-w-full select-none object-contain"
              :class="index === currentIndex ? 'cursor-grab active:cursor-grabbing' : ''"
              :style="index === currentIndex ? imageStyle : undefined"
              draggable="false"
            />
            <video
              v-else
              :ref="element => setVideoRef(element, index)"
              :src="safeItems[index]!.url"
              class="max-h-full max-w-full object-contain rounded-lg"
              playsinline
              preload="metadata"
              @click.stop="toggleCurrentVideo"
              @play="handleVideoPlay(index)"
              @pause="handleVideoPause(index)"
              @ended="handleVideoPause(index)"
            ></video>
            <div
              v-if="index === currentIndex && normalizeType(safeItems[index]!) === 'video'"
              :class="isCurrentVideoPlaying ? 'hidden' : ''"
              class="absolute inset-0 flex cursor-pointer items-center justify-center bg-black/20 transition"
              @click.stop="toggleCurrentVideo"
            >
              <Pause
                class="absolute inset-0 m-auto fill-white size-10 opacity-60"
                v-if="isCurrentVideoPlaying"
                :size="22"
              />
              <Play v-else class="absolute inset-0 m-auto fill-white size-10 opacity-60" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-xs text-white/80"
    >
      {{ safeItems.length > 0 ? `${currentIndex + 1} / ${safeItems.length}` : '0 / 0' }}
    </div>
  </div>
</template>
